import React, { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Search, Download, FileText, FileImage, Image as ImageIcon, Eye, MapPin, Clock, Globe } from "lucide-react";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { 
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";

// Sample products for the catalogue
const catalogueProducts = [
  {
    id: 1,
    name: "Organic Green Apple",
    category: "Fruits",
    price: 3.99,
    discount: 15,
    selected: false,
    image: "https://images.unsplash.com/photo-1501286353178-1ec871214838",
    isPromotional: true
  },
  {
    id: 2,
    name: "Premium Mineral Water",
    category: "Beverages",
    price: 0.99,
    discount: 10,
    selected: false,
    image: "https://images.unsplash.com/photo-1582562124811-c09040d0a901",
    isPromotional: true
  },
  {
    id: 3,
    name: "Chocolate Cookies",
    category: "Snacks",
    price: 2.49,
    discount: 20,
    selected: false,
    image: "https://images.unsplash.com/photo-1618160702438-9b02ab6515c9",
    isPromotional: true
  },
  {
    id: 4,
    name: "Fresh Bread Loaf",
    category: "Bakery",
    price: 1.99,
    discount: 5,
    selected: false,
    image: "https://images.unsplash.com/photo-1465146344425-f00d5f5c8f07",
    isPromotional: true
  },
  {
    id: 5,
    name: "Canned Tuna",
    category: "Canned Goods",
    price: 4.99,
    discount: 20,
    selected: false,
    image: "https://images.unsplash.com/photo-1501286353178-1ec871214838",
    isPromotional: false
  },
  {
    id: 6,
    name: "Milk 1L",
    category: "Dairy",
    price: 3.49,
    discount: 14,
    selected: false,
    image: "https://images.unsplash.com/photo-1582562124811-c09040d0a901",
    isPromotional: false
  }
];

// Theme options for catalogue
const catalogueThemes = [
  { 
    id: "standard", 
    name: "Standard Grid", 
    description: "Clean, professional layout with focus on products",
    background: "#ffffff", 
    primary: "#22c55e", 
    secondary: "#f3f4f6",
    textColor: "#1f2937"
  },
  { 
    id: "festive-raya", 
    name: "Hari Raya", 
    description: "Green and gold themed for Hari Raya celebrations",
    background: "#f0fdf4", 
    primary: "#15803d", 
    secondary: "#fef9c3",
    textColor: "#166534"
  },
  { 
    id: "cny", 
    name: "Chinese New Year", 
    description: "Red and gold themed for CNY celebrations",
    background: "#fef2f2", 
    primary: "#b91c1c", 
    secondary: "#fef9c3",
    textColor: "#7f1d1d" 
  },
  { 
    id: "deepavali", 
    name: "Deepavali", 
    description: "Colorful theme with purple and orange accents",
    background: "#faf5ff", 
    primary: "#7e22ce", 
    secondary: "#ffedd5",
    textColor: "#581c87" 
  },
  { 
    id: "merdeka", 
    name: "Merdeka", 
    description: "Patriotic theme with national colors",
    background: "#ffffff", 
    primary: "#0369a1", 
    secondary: "#fee2e2",
    textColor: "#0c4a6e" 
  },
  { 
    id: "cooperative", 
    name: "Cooperative Classic", 
    description: "Professional theme for cooperative stores",
    background: "#ecfccb", 
    primary: "#4d7c0f", 
    secondary: "#f3f4f6",
    textColor: "#3f6212" 
  }
];

// Sample catchphrases
const catchphrases = [
  "Let's Shop Now!",
  "Fresh Deals Await!",
  "Save More Today!",
  "Great Deals, Happy Meals!",
  "Your Savings Destination!",
  "Quality Products, Better Prices!",
  "Shop Smart, Live Better!"
];

const CatalogueGeneratorTab: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedTemplate, setSelectedTemplate] = useState("standard");
  const [products, setProducts] = useState(catalogueProducts);
  const [selectAll, setSelectAll] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [showOnlyPromotions, setShowOnlyPromotions] = useState(true);
  const [catalogueTitle, setCatalogueTitle] = useState("Weekly Special Deals");
  const [logoImage, setLogoImage] = useState<string | null>(null);
  const [selectedTheme, setSelectedTheme] = useState(catalogueThemes[0]);
  const [showPreview, setShowPreview] = useState(false);
  const [catalogueGenerated, setCatalogueGenerated] = useState(false);
  
  // Store information
  const [outletName, setOutletName] = useState("JimatMart Ampang");
  const [outletAddress, setOutletAddress] = useState("123 Jalan Ampang, 50450 Kuala Lumpur");
  const [openingHours, setOpeningHours] = useState("9:00 AM - 10:00 PM, Everyday");
  const [selectedCatchphrase, setSelectedCatchphrase] = useState(catchphrases[0]);
  const [customCatchphrase, setCustomCatchphrase] = useState("");
  const [useCatchphrase, setUseCatchphrase] = useState("preset"); // 'preset' or 'custom'
  
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  const [selectedFormats, setSelectedFormats] = useState({
    pdf: true,
    jpg: true,
    html: false
  });
  const [isDownloading, setIsDownloading] = useState(false);
  
  const filteredProducts = products.filter(product => {
    const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = categoryFilter === "all" || product.category === categoryFilter;
    const matchesPromotionFilter = showOnlyPromotions ? product.isPromotional : true;
    return matchesSearch && matchesCategory && matchesPromotionFilter;
  });

  const uniqueCategories = Array.from(new Set(products.map(product => product.category)));
  const selectedProducts = products.filter(p => p.selected);
  const selectedCount = selectedProducts.length;

  const toggleSelectAll = () => {
    const newSelectAll = !selectAll;
    setSelectAll(newSelectAll);
    setProducts(products.map(product => ({ ...product, selected: newSelectAll })));
  };

  const toggleProductSelection = (id: number) => {
    setProducts(products.map(product => 
      product.id === id ? { ...product, selected: !product.selected } : product
    ));
    
    // Update selectAll state based on all products being selected
    const allSelected = products.filter(p => p.id !== id).every(p => p.selected);
    const targetSelected = !products.find(p => p.id === id)?.selected;
    setSelectAll(allSelected && targetSelected);
  };

  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    
    if (file) {
      // Check file size (max 2MB)
      if (file.size > 2 * 1024 * 1024) {
        toast({
          variant: "destructive",
          title: "File too large",
          description: "Logo image must be less than 2MB",
        });
        return;
      }
      
      const reader = new FileReader();
      reader.onload = (event) => {
        if (event.target?.result) {
          setLogoImage(event.target.result as string);
          toast({
            title: "Logo uploaded",
            description: "Your store logo has been added to the catalogue",
          });
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const handleThemeChange = (themeId: string) => {
    const selectedTheme = catalogueThemes.find(theme => theme.id === themeId);
    if (selectedTheme) {
      setSelectedTheme(selectedTheme);
    }
  };

  const handleGenerateCatalogue = () => {
    if (selectedProducts.length === 0) {
      toast({
        variant: "destructive",
        title: "No products selected",
        description: "Please select at least one product to generate a catalogue",
      });
      return;
    }
    
    setCatalogueGenerated(true);
    toast({
      title: "Catalogue Generated",
      description: `Successfully created "${catalogueTitle}" catalogue with ${selectedProducts.length} products`,
    });
  };

  const handlePreview = () => {
    if (!catalogueGenerated) {
      toast({
        variant: "destructive",
        title: "Generate catalogue first",
        description: "Please generate the catalogue before previewing",
      });
      return;
    }
    setShowPreview(true);
  };

  const handleDownload = async () => {
    if (!catalogueGenerated) {
      toast({
        variant: "destructive",
        title: "Generate catalogue first",
        description: "Please generate the catalogue before downloading",
      });
      return;
    }
    
    setIsDownloading(true);
    
    try {
      const fileName = `${catalogueTitle.replace(/\s+/g, '-').toLowerCase()}_${new Date().toISOString().split('T')[0]}`;
      const formatPromises = [];
      
      // Process each selected format
      if (selectedFormats.jpg) {
        formatPromises.push(saveAsImage('catalogue-preview-content', `${fileName}.jpg`));
      }
      
      if (selectedFormats.pdf) {
        formatPromises.push(saveAsPdf('catalogue-preview-content', `${fileName}.pdf`));
      }
      
      if (selectedFormats.html) {
        formatPromises.push(saveAsHtml('catalogue-preview-content', `${fileName}.html`));
      }
      
      await Promise.all(formatPromises);
      
      toast({
        title: "Catalogue Downloaded",
        description: `Your catalogue has been downloaded in ${Object.entries(selectedFormats)
          .filter(([_, value]) => value)
          .map(([key]) => key.toUpperCase())
          .join(', ')} format${Object.values(selectedFormats).filter(Boolean).length > 1 ? 's' : ''}`,
      });
      
    } catch (error) {
      console.error('Download error:', error);
      toast({
        variant: "destructive",
        title: "Download failed",
        description: "There was an error creating your download. Please try again.",
      });
    } finally {
      setIsDownloading(false);
    }
  };

  // Get active catchphrase based on selection
  const getActiveCatchphrase = () => {
    return useCatchphrase === 'custom' && customCatchphrase ? 
      customCatchphrase : selectedCatchphrase;
  };

  // Function to convert DOM element to canvas
  const domToCanvas = async (element: HTMLElement): Promise<HTMLCanvasElement> => {
    const html2canvas = (await import('html2canvas')).default;
    return html2canvas(element, {
      scale: 2, // Higher quality
      useCORS: true, // Allow cross-origin images
      allowTaint: true,
      backgroundColor: selectedTheme.background
    });
  };

  // Function to save canvas as PNG/JPG
  const saveAsImage = async (elementId: string, fileName: string) => {
    const element = document.getElementById(elementId);
    if (!element) return null;
    
    const canvas = await domToCanvas(element);
    const dataUrl = canvas.toDataURL('image/jpeg', 0.8);
    
    // Create a download link and trigger it
    const link = document.createElement('a');
    link.download = fileName;
    link.href = dataUrl;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    return dataUrl;
  };

  // Function to save HTML as PDF
  const saveAsPdf = async (elementId: string, fileName: string) => {
    const element = document.getElementById(elementId);
    if (!element) return;
    
    const { jsPDF } = await import('jspdf');
    const html2canvas = (await import('html2canvas')).default;
    
    const canvas = await html2canvas(element, { 
      scale: 2,
      useCORS: true,
      allowTaint: true
    });
    
    const imgData = canvas.toDataURL('image/jpeg', 1.0);
    
    // Calculate PDF dimensions based on the canvas aspect ratio
    const pdf = new jsPDF({
      orientation: canvas.width > canvas.height ? 'landscape' : 'portrait',
      unit: 'px',
      format: [canvas.width, canvas.height]
    });
    
    // Add the image to the PDF
    pdf.addImage(imgData, 'JPEG', 0, 0, canvas.width, canvas.height);
    
    // Save the PDF
    pdf.save(fileName);
  };

  // Save HTML content
  const saveAsHtml = (elementId: string, fileName: string) => {
    const element = document.getElementById(elementId);
    if (!element) return;
    
    const htmlContent = element.outerHTML;
    const blob = new Blob([`
      <!DOCTYPE html>
      <html>
      <head>
        <title>${catalogueTitle}</title>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <style>
          body { margin: 0; font-family: system-ui, sans-serif; }
          * { box-sizing: border-box; }
        </style>
      </head>
      <body>
        ${htmlContent}
      </body>
      </html>
    `], {type: 'text/html'});
    
    // Create download link and trigger download
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row gap-4 justify-between">
        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search products..."
            className="pl-8"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="flex gap-2">
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="All Categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {uniqueCategories.map((category) => (
                <SelectItem key={category} value={category}>
                  {category}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="md:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle>Select Products for Catalogue</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-2">
                  <Checkbox 
                    id="show-promotions-only" 
                    checked={showOnlyPromotions}
                    onCheckedChange={() => setShowOnlyPromotions(!showOnlyPromotions)}
                  />
                  <Label htmlFor="show-promotions-only">
                    Show promotion products only
                  </Label>
                </div>
              </div>
              
              <div className="border rounded-md overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12">
                        <Checkbox 
                          checked={selectAll} 
                          onCheckedChange={() => toggleSelectAll()}
                          id="select-all"
                        />
                      </TableHead>
                      <TableHead>Image</TableHead>
                      <TableHead>Product Name</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead>Price</TableHead>
                      <TableHead>Discount</TableHead>
                      <TableHead>Promotion</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredProducts.length > 0 ? (
                      filteredProducts.map((product) => (
                        <TableRow key={product.id}>
                          <TableCell>
                            <Checkbox 
                              checked={product.selected} 
                              onCheckedChange={() => toggleProductSelection(product.id)}
                              id={`product-${product.id}`}
                            />
                          </TableCell>
                          <TableCell>
                            <img 
                              src={product.image} 
                              alt={product.name} 
                              className="h-10 w-10 rounded-md object-cover"
                            />
                          </TableCell>
                          <TableCell className="font-medium">{product.name}</TableCell>
                          <TableCell>{product.category}</TableCell>
                          <TableCell>RM {product.price.toFixed(2)}</TableCell>
                          <TableCell>{product.discount}%</TableCell>
                          <TableCell>
                            {product.isPromotional ? (
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                Active
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                Regular
                              </span>
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-4 text-muted-foreground">
                          No products found
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </div>

        <div>
          <Card>
            <CardHeader>
              <CardTitle>Catalogue Options</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="catalogue-title">Catalogue Title</Label>
                <Input 
                  id="catalogue-title" 
                  placeholder="Weekly Special Deals" 
                  value={catalogueTitle}
                  onChange={(e) => setCatalogueTitle(e.target.value)}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="outlet-name">Outlet Name</Label>
                <Input 
                  id="outlet-name" 
                  placeholder="JimatMart Ampang" 
                  value={outletName}
                  onChange={(e) => setOutletName(e.target.value)}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="outlet-address">Address</Label>
                <Input 
                  id="outlet-address" 
                  placeholder="123 Jalan Ampang, 50450 Kuala Lumpur" 
                  value={outletAddress}
                  onChange={(e) => setOutletAddress(e.target.value)}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="opening-hours">Opening Hours</Label>
                <Input 
                  id="opening-hours" 
                  placeholder="9:00 AM - 10:00 PM, Everyday" 
                  value={openingHours}
                  onChange={(e) => setOpeningHours(e.target.value)}
                />
              </div>
              
              <div className="space-y-2">
                <Label>Catchphrase</Label>
                <div className="flex items-center space-x-2 mb-2">
                  <Checkbox 
                    id="preset-catchphrase"
                    checked={useCatchphrase === 'preset'}
                    onCheckedChange={() => setUseCatchphrase('preset')}
                  />
                  <label htmlFor="preset-catchphrase" className="text-sm">
                    Use preset catchphrase
                  </label>
                </div>
                
                {useCatchphrase === 'preset' && (
                  <Select value={selectedCatchphrase} onValueChange={setSelectedCatchphrase}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select catchphrase" />
                    </SelectTrigger>
                    <SelectContent>
                      {catchphrases.map((phrase) => (
                        <SelectItem key={phrase} value={phrase}>
                          {phrase}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                
                <div className="flex items-center space-x-2 mb-2">
                  <Checkbox 
                    id="custom-catchphrase"
                    checked={useCatchphrase === 'custom'}
                    onCheckedChange={() => setUseCatchphrase('custom')}
                  />
                  <label htmlFor="custom-catchphrase" className="text-sm">
                    Use custom catchphrase
                  </label>
                </div>
                
                {useCatchphrase === 'custom' && (
                  <Input 
                    placeholder="Enter your custom catchphrase" 
                    value={customCatchphrase}
                    onChange={(e) => setCustomCatchphrase(e.target.value)}
                  />
                )}
              </div>
              
              <div className="space-y-2">
                <Label>Store Logo</Label>
                <div className="flex items-center gap-2">
                  <input
                    type="file"
                    ref={fileInputRef}
                    className="hidden"
                    accept="image/*"
                    onChange={handleLogoUpload}
                  />
                  
                  <Button 
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                    className="flex items-center gap-2"
                  >
                    <ImageIcon className="h-4 w-4" />
                    <span>{logoImage ? "Change Logo" : "Upload Logo"}</span>
                  </Button>
                  
                  {logoImage && (
                    <div className="h-10 w-10 rounded-md overflow-hidden">
                      <img 
                        src={logoImage} 
                        alt="Store Logo" 
                        className="h-full w-full object-contain" 
                      />
                    </div>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  Recommended size: 300x100px (max 2MB)
                </p>
              </div>
              
              <div className="space-y-2">
                <Label>Template Layout</Label>
                <Select value={selectedTemplate} onValueChange={setSelectedTemplate}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select template" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="standard">Standard Grid</SelectItem>
                    <SelectItem value="compact">Compact List</SelectItem>
                    <SelectItem value="featured">Featured Products</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Theme</Label>
                <Select 
                  value={selectedTheme.id} 
                  onValueChange={handleThemeChange}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select theme" />
                  </SelectTrigger>
                  <SelectContent>
                    {catalogueThemes.map(theme => (
                      <SelectItem key={theme.id} value={theme.id}>
                        {theme.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  {selectedTheme.description}
                </p>
                <div 
                  className="h-6 rounded-md flex items-center justify-center mt-2"
                  style={{ 
                    backgroundColor: selectedTheme.background, 
                    color: selectedTheme.textColor,
                    border: `1px solid ${selectedTheme.primary}`
                  }}
                >
                  <div className="text-xs px-2 py-1 rounded" style={{ backgroundColor: selectedTheme.primary, color: '#fff' }}>
                    Theme Preview
                  </div>
                  <div className="text-xs px-2">
                    Sample Text
                  </div>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Format</Label>
                <div className="flex flex-col gap-2">
                  <div className="flex items-center space-x-2">
                    <Checkbox 
                      id="pdf-format" 
                      checked={selectedFormats.pdf}
                      onCheckedChange={(checked) => 
                        setSelectedFormats({...selectedFormats, pdf: checked === true})}
                    />
                    <label htmlFor="pdf-format" className="text-sm font-medium leading-none">
                      PDF for Print
                    </label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox 
                      id="jpg-format" 
                      checked={selectedFormats.jpg}
                      onCheckedChange={(checked) => 
                        setSelectedFormats({...selectedFormats, jpg: checked === true})}
                    />
                    <label htmlFor="jpg-format" className="text-sm font-medium leading-none">
                      JPG for WhatsApp
                    </label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox 
                      id="html-format" 
                      checked={selectedFormats.html}
                      onCheckedChange={(checked) => 
                        setSelectedFormats({...selectedFormats, html: checked === true})}
                    />
                    <label htmlFor="html-format" className="text-sm font-medium leading-none">
                      HTML for Email
                    </label>
                  </div>
                </div>
              </div>
              
              <div className="pt-4 space-y-4">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button 
                        disabled={selectedCount === 0} 
                        className="w-full"
                        onClick={handleGenerateCatalogue}
                      >
                        <FileText className="mr-2 h-4 w-4" />
                        Generate Catalogue ({selectedCount} products)
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Create catalogue with selected products</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                
                <div className="flex gap-2 justify-center">
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          disabled={!catalogueGenerated}
                          onClick={handlePreview}
                        >
                          <Eye className="mr-1 h-4 w-4" />
                          Preview
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Preview catalogue before generating</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                  
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          disabled={!catalogueGenerated || isDownloading}
                          onClick={handleDownload}
                        >
                          {isDownloading ? (
                            <span className="flex items-center">
                              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              Processing...
                            </span>
                          ) : (
                            <>
                              <Download className="mr-2 h-4 w-4" />
                              Download
                            </>
                          )}
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Download catalogue in selected formats</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Catalogue Preview Dialog */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Catalogue Preview</DialogTitle>
            <DialogDescription>
              Preview your catalogue before downloading
            </DialogDescription>
          </DialogHeader>
          
          <div id="catalogue-preview-content" className="p-4 rounded-md" style={{ backgroundColor: selectedTheme.background }}>
            <div className="flex justify-between items-center mb-6 pb-4" style={{ borderBottom: `2px solid ${selectedTheme.primary}` }}>
              {logoImage && (
                <div className="h-16 w-32">
                  <img src={logoImage} alt="Store Logo" className="h-full w-full object-contain" />
                </div>
              )}
              <div className={`text-2xl font-bold ${logoImage ? 'text-right' : 'text-center w-full'}`} style={{ color: selectedTheme.primary }}>
                {catalogueTitle}
              </div>
            </div>
            
            {/* Outlet Information */}
            <div className="mb-6" style={{ color: selectedTheme.textColor }}>
              <h2 className="text-xl font-bold mb-2" style={{ color: selectedTheme.primary }}>
                {outletName}
              </h2>
              <div className="flex flex-col space-y-1 mb-3">
                <div className="flex items-center">
                  <MapPin className="h-4 w-4 mr-2" style={{ color: selectedTheme.primary }} />
                  <span className="text-sm">{outletAddress}</span>
                </div>
                <div className="flex items-center">
                  <Clock className="h-4 w-4 mr-2" style={{ color: selectedTheme.primary }} />
                  <span className="text-sm">{openingHours}</span>
                </div>
              </div>
              <div className="text-lg font-medium italic text-center py-2 px-4 mb-4 rounded-md" 
                style={{ 
                  backgroundColor: `${selectedTheme.secondary}`, 
                  color: selectedTheme.textColor,
                  border: `1px dashed ${selectedTheme.primary}`
                }}>
                {getActiveCatchphrase()}
              </div>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {selectedProducts.map((product) => (
                <div 
                  key={product.id} 
                  className="rounded-lg overflow-hidden shadow-sm"
                  style={{ backgroundColor: selectedTheme.secondary, borderColor: selectedTheme.primary }}
                >
                  <div className="aspect-[4/3] overflow-hidden relative">
                    <img src={product.image} alt={product.name} className="w-full h-full object-cover" />
                    <div 
                      className="absolute top-0 right-0 p-2 rounded-bl-lg font-bold"
                      style={{ backgroundColor: selectedTheme.primary, color: 'white' }}
                    >
                      {product.discount}% OFF
                    </div>
                  </div>
                  <div className="p-3" style={{ color: selectedTheme.textColor }}>
                    <h3 className="font-medium text-sm">{product.name}</h3>
                    <div className="flex justify-between items-center mt-2">
                      <div>
                        <span className="text-lg font-bold">
                          RM {(product.price * (1 - product.discount/100)).toFixed(2)}
                        </span>
                        <span className="text-xs line-through ml-1 opacity-70">
                          RM {product.price.toFixed(2)}
                        </span>
                      </div>
                      <div className="text-xs py-1 px-2 rounded-full" style={{ backgroundColor: selectedTheme.primary, color: 'white' }}>
                        {product.category}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="mt-6 pt-4 text-center" style={{ borderTop: `1px solid ${selectedTheme.primary}`, color: selectedTheme.textColor }}>
              <p className="text-sm">Valid from 01/06/2025 to 14/06/2025. While stocks last.</p>
              <div className="flex items-center justify-center space-x-2 mt-2">
                <Globe className="h-4 w-4" style={{ color: selectedTheme.primary }} />
                <p className="text-sm">Visit our store or call us at 03-12345678 for more information.</p>
              </div>
              <p className="mt-4 text-sm font-medium" style={{ color: selectedTheme.primary }}>
                Thank you for shopping with us!
              </p>
            </div>
          </div>
          
          <DialogFooter className="gap-2">
            <Button onClick={() => setShowPreview(false)} variant="outline">
              Close
            </Button>
            <Button onClick={handleDownload} disabled={isDownloading}>
              {isDownloading ? (
                <span className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Processing...
                </span>
              ) : (
                <>
                  <Download className="mr-2 h-4 w-4" />
                  Download
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CatalogueGeneratorTab;
